import { apiClient } from "@/lib/apiClient";
import type {
  EmailDetail,
  FilterState,
  ListEmailsResponse,
  ListTab,
  ListTabCounts,
  ParcelSummary,
} from "@/types/domain";

/** Translate the tab enum to backend ``approvalStatus`` + ``categorization`` query params. */
export function tabToEmailQuery(tab: ListTab): {
  approvalStatus?: string;
  categorization?: string;
} {
  switch (tab) {
    case "all":
      return {};
    case "needsCategorization":
      return { categorization: "uncategorized" };
    case "needsReview":
      // Intentionally no categorization constraint — an uncategorized
      // email with pending-review docs should appear in both tabs.
      return { approvalStatus: "pending_review" };
    case "approved":
      return { approvalStatus: "approved" };
    case "archived":
      return { approvalStatus: "archived" };
    case "failed":
      return { approvalStatus: "failed" };
  }
}

function addSharedFilterParams(params: URLSearchParams, filters: FilterState): void {
  if (filters.subject) params.set("subject", filters.subject);
  if (filters.from) params.set("from", filters.from);
  if (filters.documentType) params.set("documentType", filters.documentType);
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  if (filters.tradeKey) params.set("tradeKey", filters.tradeKey);
}

function buildQuery(filters: FilterState, skipToken: string | null, top = 50): string {
  const params = new URLSearchParams();
  addSharedFilterParams(params, filters);
  const tabQuery = tabToEmailQuery(filters.listTab);
  if (tabQuery.approvalStatus) params.set("approvalStatus", tabQuery.approvalStatus);
  if (tabQuery.categorization) params.set("categorization", tabQuery.categorization);
  params.set("sort", filters.sort);
  params.set("top", String(top));
  if (skipToken) params.set("skipToken", skipToken);
  return params.toString();
}

export function listEmails(
  filters: FilterState,
  skipToken: string | null,
  top = 50,
  signal?: AbortSignal,
): Promise<ListEmailsResponse> {
  return apiClient.get<ListEmailsResponse>(
    `/api/emails?${buildQuery(filters, skipToken, top)}`,
    { signal },
  );
}

export function getEmail(id: string): Promise<EmailDetail> {
  return apiClient.get<EmailDetail>(`/api/emails/${encodeURIComponent(id)}`);
}

export function patchEmail(
  id: string,
  body: { tradeKey?: string | null },
): Promise<EmailDetail> {
  return apiClient.patch<EmailDetail>(
    `/api/emails/${encodeURIComponent(id)}`,
    body,
  );
}

export function setSelectedParcelOnEmail(
  id: string,
  parcelNumber: number,
): Promise<EmailDetail> {
  return apiClient.patch<EmailDetail>(
    `/api/emails/${encodeURIComponent(id)}/selected-parcel`,
    { parcelNumber },
  );
}

export function refetchEmailParcels(id: string): Promise<EmailDetail> {
  return apiClient.post<EmailDetail>(
    `/api/emails/${encodeURIComponent(id)}/parcel-fetch`,
    {},
  );
}

export interface EmailRemoveResponse {
  emailId: string;
  documentsRemoved: number;
  documentsSkipped: number;
}

export function removeEmail(id: string): Promise<EmailRemoveResponse> {
  return apiClient.post<EmailRemoveResponse>(
    `/api/emails/${encodeURIComponent(id)}/remove`,
    {},
  );
}

/** Fetch enriched parcel candidate metadata (trade/qty/dates) for the
 * email's stored candidate set, hydrated on demand from Symphony.
 */
export function getEmailParcelCandidateDetails(
  id: string,
): Promise<ParcelSummary[]> {
  return apiClient.get<ParcelSummary[]>(
    `/api/emails/${encodeURIComponent(id)}/parcel-candidates/details`,
  );
}

/** Per-tab email counts — shared filters apply but the tab axis is ignored. */
export function getEmailCounts(filters: FilterState): Promise<ListTabCounts> {
  const params = new URLSearchParams();
  addSharedFilterParams(params, filters);
  const q = params.toString();
  const url = q ? `/api/emails/counts?${q}` : "/api/emails/counts";
  return apiClient.get<ListTabCounts>(url);
}

export { buildQuery };
